import { skipToken } from '@reduxjs/toolkit/query'
import React from 'react'
import { useGetBillingChargeQuery } from 'store/services/billingCharges'
import {
  ChargePayerChainEntry,
  deriveNextResponsible,
  getChargePayerChain,
  NextResponsibleParty,
} from 'ts-common/utils/serviceLineBalances.utils'

export type ChargePayerOption = {
  value: number | string
  label: string
  payerName: string
  // The remittance's name for this payer, when it is not ours.
  matchedName?: string | null
}

// The posting-preview logic both payment modals share: which payers a selected
// charge allows in Payment By, and who becomes responsible after each of them
// pays. It must agree with the server's deriveNextResponsible, so it lives in
// exactly one place.
//
// The chain lives on the charge aggregate, not on the listing row, so the
// selected charge is fetched on its own; the row's primary/secondary stand in
// while it loads, and `isChainLoaded` tells callers when the real chain (the
// only one that carries positions 3+) has arrived.
export const useChargePayerChain = ({
  patientId,
  listedCharges,
}: {
  patientId?: number | string
  listedCharges?: unknown[]
}) => {
  const [selectedChargeId, setSelectedChargeId] = React.useState<string | undefined>(undefined)

  const { data: selectedChargeDetails } = useGetBillingChargeQuery(
    selectedChargeId && patientId
      ? { id: selectedChargeId, patientId: String(patientId) }
      : skipToken,
  )

  const isChainLoaded = React.useCallback(
    (chargeId?: string) =>
      Boolean(chargeId) && String(selectedChargeDetails?.id ?? '') === String(chargeId ?? ''),
    [selectedChargeDetails],
  )

  const getChargeChain = React.useCallback(
    (chargeId?: string): ChargePayerChainEntry[] => {
      const listedCharge = ((listedCharges ?? []) as any[]).find(
        (c) => String(c.id) === String(chargeId),
      )
      const charge = isChainLoaded(chargeId) ? selectedChargeDetails ?? listedCharge : listedCharge

      return charge ? getChargePayerChain(charge as any) : []
    },
    [listedCharges, selectedChargeDetails, isChainLoaded],
  )

  // With a charge selected, Payment By is restricted to that charge's payer
  // chain (matching the derived next-responsible and payer-position logic).
  // Charges without payers (e.g. self-pay quick charges) keep the full list.
  const getChargePayerOptions = React.useCallback(
    (chargeId?: string): ChargePayerOption[] | null => {
      const chain = getChargeChain(chargeId)

      if (!chain.length) {
        return null
      }

      return chain.map((payer) => ({
        value: payer.payerId,
        label: `${payer.position}. ${payer.payerName || 'Unknown payer'}`,
        payerName: payer.payerName || 'Unknown payer',
      }))
    },
    [getChargeChain],
  )

  // Read-only: the payer after the one that paid, or the patient once the chain
  // runs out. The server enforces the same rule.
  const getDerivedNextResponsible = React.useCallback(
    (chargeId?: string, paymentByPayerId?: number): NextResponsibleParty =>
      deriveNextResponsible({ paymentByPayerId, payers: getChargeChain(chargeId) }),
    [getChargeChain],
  )

  const getNextResponsibleLabel = React.useCallback(
    (chargeId?: string, paymentByPayerId?: number): string => {
      const derived = getDerivedNextResponsible(chargeId, paymentByPayerId)

      return derived.type === 'PAYER' ? derived.payerName || 'Next payer' : 'Patient'
    },
    [getDerivedNextResponsible],
  )

  return {
    selectedChargeId,
    setSelectedChargeId,
    isChainLoaded,
    getChargeChain,
    getChargePayerOptions,
    getDerivedNextResponsible,
    getNextResponsibleLabel,
  }
}

// Reconciles Payment By with the selected charge's chain only once the real
// chain has loaded — deciding on the listing-row fallback would wipe a
// legitimately selected position-3 payer the fallback cannot see. Rendered
// inside the Formik body, where the field values live.
export const ChargeChainPaymentBySync: React.FC<{
  chargeId?: string
  paymentByPayerId?: number | string
  chainLoaded: boolean
  chain: ChargePayerChainEntry[]
  setFieldValue: (field: string, value: unknown) => void
}> = ({ chargeId, paymentByPayerId, chainLoaded, chain, setFieldValue }) => {
  React.useEffect(() => {
    if (!chargeId || !chainLoaded || !chain.length || !paymentByPayerId) {
      return
    }

    if (!chain.some((payer) => Number(payer.payerId) === Number(paymentByPayerId))) {
      setFieldValue('paymentByPayerId', undefined)
    }
  }, [chargeId, paymentByPayerId, chainLoaded, chain, setFieldValue])

  return null
}
